import React from 'react';

import { v4 as uuid } from 'uuid';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IconButton } from '@mui/material';

import { Button } from 'components/Button';

import { ProtectorType } from 'core/constants';

import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { EnumOptions } from 'utils/api/modules/entities/entities.types';

import EntitiesFormField from 'containers/EntitiesFormSubmission/components/EntitiesFormField/EntitiesFormField';

import CascadeField from './CascadeField';
import { EnumOption, optionShownFor } from './cascade';
import { RepeatingGroupErrors } from './repeatingGroupValidation';
import {
  Wrapper,
  EmptyState,
  EntryCard,
  EntryHeader,
  EntryTitle,
  SubField,
  SubFieldLabel,
  SubFieldError,
  Footer,
  Total,
} from './RepeatingGroupField.styles';

type Entry = Record<string, AnswerTypes>;

interface SubFieldDef {
  questionType: ProtectorType;
  description: string;
  required?: boolean;
  properties?: {
    enum?: EnumOption[];
    dependsOn?: string;
  };
}

export interface RepeatingProperties {
  noun: string;
  nounTitle: string;
  nounPlural: string;
  minRows: number;
  maxRows: number;
  order: string[];
  properties: Record<string, SubFieldDef>;
}

interface RepeatingGroupFieldProps {
  questionKey: string;
  properties: RepeatingProperties;
  value?: Entry[];
  onChange: (entries: Entry[]) => void;
  errors?: RepeatingGroupErrors;
}

export const RepeatingGroupField = ({
  questionKey,
  properties,
  value,
  onChange,
  errors,
}: RepeatingGroupFieldProps): React.ReactElement => {
  const { order, properties: subDefs, noun, nounTitle, nounPlural, maxRows } = properties;

  // Local working copy; `value` lags by the autosave debounce. Once edited, ignore value echoes so a
  // stale one can't revert a field mid-edit (e.g. Comments) — take `value` only for the initial load.
  const [entries, setEntries] = React.useState<Entry[]>(value ?? []);
  const edited = React.useRef(false);
  const [entryKeys, setEntryKeys] = React.useState<string[]>(() => (value ?? []).map(() => uuid()));

  React.useEffect(() => {
    if (edited.current) return;
    const next = value ?? [];
    setEntries(next);
    setEntryKeys(next.map(() => uuid()));
  }, [value]);

  const propagate = (next: Entry[]): void => {
    edited.current = true;
    setEntries(next);
    onChange(next);
  };

  const addEntry = (): void => {
    setEntryKeys((prev) => [...prev, uuid()]);
    propagate([...entries, {}]);
  };

  const removeEntry = (idx: number): void => {
    setEntryKeys((prev) => prev.filter((_, i) => i !== idx));
    propagate(entries.filter((_, i) => i !== idx));
  };

  const updateSubField = (idx: number, subKey: string, subValue: AnswerTypes): void => {
    const next = entries.map((e) => ({ ...e }));
    next[idx][subKey] = subValue;

    // Clear any dependent child of this sub-field whose option no longer belongs to the newly
    // chosen parent value (mirrors the top-level clear-on-parent-change).
    const newParentKey = Array.isArray(subValue) && subValue.length ? (subValue[0] as string) : undefined;
    order.forEach((childKey) => {
      if (subDefs[childKey].properties?.dependsOn === subKey) {
        const childVal = next[idx][childKey] as string[] | undefined;
        if (childVal && childVal.length) {
          const opt = (subDefs[childKey].properties?.enum ?? []).find((o) => o.key === childVal[0]);
          if (!opt || !optionShownFor(opt, newParentKey)) {
            next[idx][childKey] = [];
          }
        }
      }
    });

    propagate(next);
  };

  const testId = (suffix: string): string => `repeating-group-${questionKey}-${suffix}`;

  const renderSubField = (entry: Entry, idx: number, subKey: string): React.ReactElement => {
    const def = subDefs[subKey];
    const options = def.properties?.enum ?? [];
    const dependsOn = def.properties?.dependsOn;
    const hasError = !!errors?.[idx]?.[subKey];

    if (dependsOn) {
      return (
        <CascadeField
          type={def.questionType}
          description={def.description}
          options={options}
          dependsOn={dependsOn}
          parentValue={entry[dependsOn] as string[] | undefined}
          value={entry[subKey]}
          onChange={(v) => updateSubField(idx, subKey, v)}
          requiredNotFilled={hasError}
        />
      );
    }

    return (
      <EntitiesFormField
        type={def.questionType}
        description={def.description}
        items={options as unknown as EnumOptions}
        value={entry[subKey] ?? ''}
        onChange={(v: AnswerTypes) => updateSubField(idx, subKey, v)}
        requiredNotFilled={hasError}
      />
    );
  };

  if (entries.length === 0) {
    return (
      <Wrapper data-testid={testId('container')}>
        <EmptyState>
          <p>No {nounPlural} added yet.</p>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={addEntry}
            data-testid={testId('add')}
          >
            Add {noun}
          </Button>
        </EmptyState>
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid={testId('container')}>
      {entries.map((entry, idx) => (
        <EntryCard key={entryKeys[idx]} data-testid={testId(`entry-${idx}`)}>
          <EntryHeader>
            <EntryTitle>
              {nounTitle} {idx + 1}
            </EntryTitle>
            <IconButton
              aria-label={`Remove ${noun}`}
              onClick={() => removeEntry(idx)}
              size="small"
              data-testid={testId(`entry-${idx}-remove`)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </EntryHeader>

          {order.map((subKey) => {
            const fieldError = errors?.[idx]?.[subKey];
            return (
              <SubField key={subKey} data-testid={testId(`entry-${idx}-${subKey}`)}>
                <SubFieldLabel $required={subDefs[subKey].required}>{subDefs[subKey].description}</SubFieldLabel>
                {renderSubField(entry, idx, subKey)}
                {fieldError && <SubFieldError>{fieldError}</SubFieldError>}
              </SubField>
            );
          })}
        </EntryCard>
      ))}

      <Footer>
        <Total>
          Total: {entries.length} {entries.length === 1 ? noun : nounPlural}
        </Total>
        {entries.length < maxRows && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={addEntry}
            data-testid={testId('add')}
          >
            Add {noun}
          </Button>
        )}
      </Footer>
    </Wrapper>
  );
};

export default RepeatingGroupField;
